import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, of, switchMap } from 'rxjs';
import { GraphQlService } from '@shared/services/graphql-service';
import { ApolloNamespace } from '@shared/enums/apollo-namespaces';
import {
  GET_MY_NOTIFICATIONS,
  GET_MY_UNREAD_NOTIFICATIONS,
  GET_MY_UNREAD_NOTIFICATION_COUNT,
  MARK_ALL_MY_NOTIFICATIONS_AS_READ,
  MARK_NOTIFICATION_AS_READ,
} from '../graphql/service-selection.graphql';
import {
  MyNotificationsResponse,
  MyUnreadNotificationsResponse,
  MyUnreadNotificationCountResponse,
  MarkAllMyNotificationsAsReadResponse,
  MarkNotificationAsReadResponse,
} from '../responses/notifications.response';
import {
  LoadMyNotificationsActions,
  LoadMyUnreadNotificationsActions,
  LoadMyUnreadNotificationCountActions,
  MarkAllMyNotificationsAsReadActions,
  MarkNotificationAsReadActions,
} from './service-selection-notifications.actions';

@Injectable()
export class ServiceSelectionNotificationsEffects {
  private readonly actions$ = inject(Actions);
  private readonly graphqlService = inject(GraphQlService);

  loadNotifications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadMyUnreadNotificationsActions.request),
      switchMap(({ request }) =>
        this.graphqlService
          .query<MyUnreadNotificationsResponse>({
            query: GET_MY_UNREAD_NOTIFICATIONS,
            variables: {
              pageableParam: request.pageableParam,
            },
            apolloNamespace: ApolloNamespace.notifications,
          })
          .pipe(
            map((response) => {
              const data = response.data?.myUnreadNotifications;
              return LoadMyUnreadNotificationsActions.success({
                response: {
                  content: data?.content ?? [],
                  hasNext: data?.hasNext ?? false,
                  page: request.pageableParam?.first ?? 0,
                },
              });
            }),
            catchError((error) => of(LoadMyUnreadNotificationsActions.failure({ error }))),
          ),
      ),
    ),
  );

  loadAllNotifications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadMyNotificationsActions.request),
      switchMap(({ request }) =>
        this.graphqlService
          .query<MyNotificationsResponse>({
            query: GET_MY_NOTIFICATIONS,
            variables: {
              pageableParam: request.pageableParam,
            },
            apolloNamespace: ApolloNamespace.notifications,
          })
          .pipe(
            map((response) => {
              const data = response.data?.myNotifications;
              return LoadMyNotificationsActions.success({
                response: {
                  content: data?.content ?? [],
                  hasNext: data?.hasNext ?? false,
                  page: request.pageableParam?.first ?? 0,
                },
              });
            }),
            catchError((error) => of(LoadMyNotificationsActions.failure({ error }))),
          ),
      ),
    ),
  );

  loadCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadMyUnreadNotificationCountActions.request),
      switchMap(() =>
        this.graphqlService
          .query<MyUnreadNotificationCountResponse>({
            query: GET_MY_UNREAD_NOTIFICATION_COUNT,
            apolloNamespace: ApolloNamespace.notifications,
          })
          .pipe(
            map((response) =>
              LoadMyUnreadNotificationCountActions.success({
                response: response.data?.myUnreadNotificationCount ?? 0,
              }),
            ),
            catchError((error) => of(LoadMyUnreadNotificationCountActions.failure({ error }))),
          ),
      ),
    ),
  );

  markAllRead$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MarkAllMyNotificationsAsReadActions.request),
      switchMap(() =>
        this.graphqlService
          .mutate<MarkAllMyNotificationsAsReadResponse>({
            mutation: MARK_ALL_MY_NOTIFICATIONS_AS_READ,
            apolloNamespace: ApolloNamespace.notifications,
          })
          .pipe(
            map((response) =>
              MarkAllMyNotificationsAsReadActions.success({
                response: response.data?.markAllMyNotificationsAsRead,
              }),
            ),
            catchError((error) => of(MarkAllMyNotificationsAsReadActions.failure({ error }))),
          ),
      ),
    ),
  );

  markRead$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MarkNotificationAsReadActions.request),
      switchMap(({ request }) =>
        this.graphqlService
          .mutate<MarkNotificationAsReadResponse>({
            mutation: MARK_NOTIFICATION_AS_READ,
            variables: { uuid: request.uuid },
            apolloNamespace: ApolloNamespace.notifications,
          })
          .pipe(
            map((response) =>
              MarkNotificationAsReadActions.success({
                response: response.data?.markNotificationAsRead,
              }),
            ),
            catchError((error) => of(MarkNotificationAsReadActions.failure({ error }))),
          ),
      ),
    ),
  );
}
